'use client';

import { formatStructuredAddress } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  getIntakeMovingDate,
  getIntakeDeparture,
  getIntakeArrival,
  getIntakePacking,
  getIntakeCustomerRequests,
} from '@/entities/visit-counsel/model/intake-helpers';
import type { MovingMethodRequest } from '@/entities/visit-counsel/model/intake.types';
import type { MovingTypeSpecificationResponse } from '@/entities/visit-counsel/model/moving-type-intake.types';
import { formatMovingTypeSelectedTypesForDisplay } from '@/entities/visit-counsel/model/moving-type-display';
import { SPACE_TYPE_LABELS } from './counsel-progress.constants';

const PACKING_LABELS: Record<string, string> = {
  FULL_PACKING: '전체 포장',
  PARTIAL_PACKING: '반포장',
  NO_PACKING: '일반(비포장)',
};

const MAX_ADDRESS_LEN = 72;

function truncateText(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function formatMovingDateLine(isoDate: string): string {
  const parts = isoDate.split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return isoDate;
  const [y, m, d] = parts;
  return `${y}년 ${m}월 ${d}일`;
}

function formatMovingMethodSuffix(method: MovingMethodRequest | null | undefined): string {
  if (!method) return '';
  const attrs = method.attributes ?? {};
  switch (method.type) {
    case 'LADDER_TRUCK':
    case 'SKY_LIFT': {
      const s = attrs.entranceSpace?.trim();
      return s ? `들어오는 공간 ${s}` : '';
    }
    case 'FIRST_FLOOR': {
      const bits: string[] = [];
      if (attrs.ramp === 'true') bits.push('경사로');
      if (attrs.stairCount?.trim()) bits.push(`계단 ${attrs.stairCount}칸`);
      return bits.join(' · ');
    }
    default:
      return '';
  }
}

function formatSiteSummary(
  counsel: VisitCounselView | null | undefined,
  kind: 'departure' | 'arrival'
): string | undefined {
  const data = kind === 'departure' ? getIntakeDeparture(counsel) : getIntakeArrival(counsel);
  if (!data) return undefined;

  const parts: string[] = [];
  if (data.address) {
    parts.push(truncateText(formatStructuredAddress(data.address), MAX_ADDRESS_LEN));
  }
  if (typeof data.supplyArea === 'number' && data.supplyArea > 0) {
    parts.push(`${data.supplyArea}평`);
  }
  if (data.hasElevator === true) parts.push('엘리베이터 있음');
  else if (data.hasElevator === false) parts.push('엘리베이터 없음');

  const methodType = data.movingMethod?.type;
  if (methodType && SPACE_TYPE_LABELS[methodType]) {
    parts.push(SPACE_TYPE_LABELS[methodType]);
  }
  const extra = formatMovingMethodSuffix(data.movingMethod);
  if (extra) parts.push(extra);

  const line = parts.join(' · ').trim();
  return line || undefined;
}

export type GetIntakeChecklistDescriptionOptions = {
  /** 이사 유형 코드 → `name` 매핑용 (없으면 코드별 한글 폴백) */
  movingTypeSpecs?: MovingTypeSpecificationResponse[] | null;
};

/**
 * 상담 진행 체크리스트 인테이크 하위 항목에 표시할 요약 문구
 */
export function getIntakeChecklistDescription(
  counsel: VisitCounselView | null | undefined,
  key: 'date' | 'departure' | 'arrival' | 'packing' | 'movingType' | 'customer',
  options?: GetIntakeChecklistDescriptionOptions
): string | undefined {
  switch (key) {
    case 'date': {
      const d = getIntakeMovingDate(counsel);
      return d ? formatMovingDateLine(d) : undefined;
    }
    case 'departure':
      return formatSiteSummary(counsel, 'departure');
    case 'arrival':
      return formatSiteSummary(counsel, 'arrival');
    case 'packing': {
      const p = getIntakePacking(counsel);
      const method = p?.method;
      return method && PACKING_LABELS[method] ? PACKING_LABELS[method] : undefined;
    }
    case 'movingType':
      return formatMovingTypeSelectedTypesForDisplay(
        counsel?.movingTypeIntake?.selectedTypes,
        options?.movingTypeSpecs
      );
    case 'customer': {
      const cr = getIntakeCustomerRequests(counsel);
      if (!cr) return undefined;
      const q = cr.questions?.length ?? 0;
      const paid = cr.paidQuoteRequests?.length ?? 0;
      if (q === 0 && paid === 0) return '없음';
      const parts: string[] = [];
      if (q > 0) parts.push(`요청 ${q}건`);
      if (paid > 0) parts.push(`비용 필요 ${paid}건`);
      return parts.join(' · ');
    }
    default:
      return undefined;
  }
}
